import moment from 'moment';
import type {
  CalculatedFoodPrice,
  OrderQuantityPricing,
  PricedOrderItem,
  ScalarItemPricing,
  VendorPricing,
  VendorPricingByCode,
} from 'types';
import { mapKey } from 'utils';

import { PROGRAM_VENDOR_CONFIG, VENDORS } from './vendorConfig';
import type { VendorDefaultPrice } from './vendorConfig';

// prepareMealEquivalents is not in use now
export function prepareMealEquivalents(vendor: string, ordersItems: PricedOrderItem[]): number {
  let quantity = 0;
  // eslint-disable-next-line no-constant-condition
  if (true) {
    switch (vendor) {
      case 'PK':
        ordersItems.forEach((item) => {
          quantity +=
            parseInt(String(item?.quantity ? item.quantity : 1)) *
            parseInt(String(item?.noOfServings ? item.noOfServings : 1));
        });

        break;
      case 'SB':
        ordersItems.forEach((item) => {
          quantity += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
        });
        break;

      default:
        ordersItems.forEach((item) => {
          quantity += parseInt(String(item.quantity));
        });
        return quantity;
    }
  }
  return quantity;
}

// prepareMEQ is not in use now
export function prepareMEQ(
  organization: string,
  vendor: string,
  ordersItems: PricedOrderItem[],
  vendorsFoodPrice: VendorPricingByCode,
): OrderQuantityPricing {
  // Annotated rather than inferred: the initialiser's `itemPricingnFees: 0` would
  // infer `number`, but `validateOrderQuantity` returns the full union and assigns
  // it straight back here.
  let orderPrice: OrderQuantityPricing = {
    quantity: 0,
    itemPricingnFees: 0,
    message: '',
    hasErrors: false,
  };

  if (ordersItems && ordersItems.length) {
    orderPrice = validateOrderQuantity(
      organization,
      vendor,
      ordersItems,
      vendorsFoodPrice,
      orderPrice,
      undefined,
    );
  } else {
    orderPrice = {
      quantity: 0,
      itemPricingnFees: 0,
      message: 'No item selected!',
      hasErrors: true,
    };
  }
  return orderPrice;
}

/**
 * `organization` admits the empty spellings: the catalogue reaches this from a screen
 * that renders with `org={null}` in the roster-wide mode (`components/home/index.tsx`
 * through `components/common/food/index.tsx`). It is inert to widen — the parameter is
 * threaded to `validateOrderQuantity` and never read by either body.
 */
export function prepareOrderQuantity(
  organization: string | null | undefined,
  vendor: string,
  ordersItems: PricedOrderItem[],
  vendorsFoodPrice: VendorPricingByCode,
  bundleName: string | undefined,
): OrderQuantityPricing {
  // Annotated rather than inferred, for the same reason as `prepareMEQ` above.
  let orderPrice: OrderQuantityPricing = {
    quantity: 0,
    itemPricingnFees: 0,
    message: '',
    hasErrors: false,
  };

  if (ordersItems && ordersItems.length) {
    orderPrice = validateOrderQuantity(
      organization,
      vendor,
      ordersItems,
      vendorsFoodPrice,
      orderPrice,
      bundleName,
    );
  } else {
    orderPrice = {
      quantity: 0,
      itemPricingnFees: 0,
      message: 'No item selected!',
      hasErrors: true,
    };
  }

  return orderPrice;
}

/**
 * SUSPECTED DEFECT (unguarded pricing lookup): the guard below admits a vendor by
 * EITHER having a `vendorsFoodPrice[vendor]` entry OR being named in the literal
 * list. Four branches then dereference a pricing lookup without re-checking it, so
 * a vendor admitted only by name throws `TypeError: Cannot read properties of
 * undefined (reading 'itemPrice')`:
 *
 *   - `FFH`  line ~163  `ffhPricing.itemPrice[...]`      — admitted by name, no try/catch
 *   - `EF`   line ~183  `EFPrice.itemPrice[...]`         — admitted by name, no try/catch
 *   - `ML`   line ~425  `mlPricing.itemPrice[...]`       — admitted by name, no try/catch
 *   - default line ~478 `defaultPricing.itemPrice[...]`  — reached by `FFSH`/`SSHE`/`NU`/
 *                                                          `CCF`/`HH`/`MM`, all admitted
 *                                                          by name, no try/catch
 *
 * `MH` (line ~347) has the same hole but sits inside a `try`, so it degrades to
 * "Error - Price is missing for the food item" instead of throwing.
 *
 * Not fixed here: adding a guard would convert a crash into a silent no-op, which is
 * a behaviour change. The `VendorPricingByCode` annotation makes each of these visible
 * as a TS18048 "possibly undefined" at the dereference.
 */
export function validateOrderQuantity(
  organization: string | null | undefined,
  vendor: string,
  ordersItems: PricedOrderItem[],
  vendorsFoodPrice: VendorPricingByCode,
  orderPrice: OrderQuantityPricing,
  _bundleName: string | undefined,
): OrderQuantityPricing {
  if (
    (vendorsFoodPrice && vendorsFoodPrice[vendor]) ||
    vendor === 'MF' ||
    vendor === VENDORS.CJ ||
    vendor === VENDORS.EF ||
    vendor === 'GB' ||
    vendor === 'OK' ||
    vendor === 'MH' ||
    vendor === 'ML' ||
    vendor === 'FFH' ||
    vendor === 'FFSH' ||
    vendor === 'SSHE' ||
    vendor === 'NU' ||
    vendor === VENDORS.CCF ||
    vendor === VENDORS.HH ||
    vendor === VENDORS.MM
  ) {
    switch (vendor) {
      case 'PK': {
        const pkPricing = vendorsFoodPrice['PK'];
        let pkQuantity = 0;
        ordersItems.forEach((item) => {
          pkQuantity += parseInt(String(item.quantity));
        });

        orderPrice.quantity = pkQuantity;

        if (pkPricing!.itemPrice[pkQuantity] && pkPricing!.itemPrice[pkQuantity] !== undefined) {
          /*
           * Built in one expression rather than seeded with `{ itemPrice: {},
           * shippingFees: {} }` and then overwritten field by field. The seed's empty
           * records made the value a `VendorPricing` that the next two statements
           * immediately turned into a `ScalarItemPricing`, so no single type described
           * it and both assignments reported "number is not assignable to
           * Record<string | number, number>". The two resulting numbers are the same
           * arithmetic on the same operands, in the same order.
           */
          orderPrice.itemPricingnFees = {
            itemPrice: Math.round(pkPricing!.itemPrice[pkQuantity]! * pkQuantity * 100) / 100,
            shippingFees:
              (pkPricing!.shippingFees[pkQuantity] ? pkPricing!.shippingFees[pkQuantity]! : 0) *
              pkQuantity,
          };
        } else {
          orderPrice.hasErrors = true;
          const quantities = Object.keys(pkPricing!.itemPrice);
          orderPrice.message = `Error - Please only order PK in quantities of ${quantities.join(', ')}`;
        }

        break;
      }
      case 'CJ': {
        let CJQuantity = 0;
        let CJPrice = 0;

        ordersItems.forEach((item) => {
          CJQuantity += parseInt(String(item.quantity));
        });

        try {
          ordersItems.forEach((item) => {
            CJPrice +=
              Math.round(
                parseFloat(String(item.itemPrice)) * parseInt(String(item.quantity)) * 100,
              ) / 100;
          });
          CJPrice = Math.round(CJPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = CJQuantity;

        if (CJPrice) {
          orderPrice.itemPricingnFees = CJPrice;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
        }
        break;
      }
      case 'FFH': {
        const ffhPricing = vendorsFoodPrice['FFH'];
        let ffhQuantity = 0;

        ordersItems.forEach((item) => {
          ffhQuantity += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
        });

        orderPrice.quantity = ffhQuantity;

        if (
          ffhPricing!.itemPrice[ffhQuantity] &&
          ffhPricing!.itemPrice[ffhQuantity] !== undefined
        ) {
          orderPrice.itemPricingnFees = ffhPricing!;
        } else {
          orderPrice.hasErrors = true;
          const quantities = Object.keys(ffhPricing!.itemPrice);
          orderPrice.message = `Error - Please only order FFH in quantities of ${quantities.join(', ')} SERVINGS`;
        }
        break;
      }
      case 'EF': {
        let EFQuantity = 0;
        const EFPrice = vendorsFoodPrice['EF'];

        ordersItems.forEach((item) => {
          EFQuantity += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
        });

        orderPrice.quantity = EFQuantity;

        if (EFPrice!.itemPrice[EFQuantity] && EFPrice!.itemPrice[EFQuantity] !== undefined) {
          orderPrice.itemPricingnFees = EFPrice!;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Issue with EF order quantity or pricing!`;
        }
        break;
      }
      case 'EW': {
        const ewPricing = vendorsFoodPrice['EW'];
        let ewQuantity = 0;
        ordersItems.forEach((item) => {
          ewQuantity += parseInt(String(item.quantity));
        });

        orderPrice.quantity = ewQuantity;

        if (ewPricing!.itemPrice[ewQuantity] && ewPricing!.itemPrice[ewQuantity] !== undefined) {
          orderPrice.itemPricingnFees = ewPricing!;
        } else {
          orderPrice.hasErrors = true;
          const quantities = Object.keys(ewPricing!.itemPrice);
          orderPrice.message = `Error - Please only order EW in quantities of ${quantities.join(', ')}`;
        }

        break;
      }
      case 'SB': {
        const sbPricing = vendorsFoodPrice['SB'];
        let sbQuantity = 0;
        ordersItems.forEach((item) => {
          sbQuantity += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
        });

        orderPrice.quantity = sbQuantity;

        if (sbPricing!.itemPrice[sbQuantity] && sbPricing!.itemPrice[sbQuantity] !== undefined) {
          orderPrice.itemPricingnFees = sbPricing!;
        } else {
          orderPrice.hasErrors = true;
          const quantities = Object.keys(sbPricing!.itemPrice);
          orderPrice.message = `Error - Please only order SB in quantities of ${quantities.join(', ')} SERVINGS`;
        }
        break;
      }

      case 'MF': {
        let mfQuantity = 0;
        let mfPrice = 0;

        ordersItems.forEach((item) => {
          mfQuantity += parseInt(String(item.quantity));
        });

        try {
          ordersItems.forEach((item) => {
            mfPrice +=
              Math.round(
                parseFloat(String(item.itemPrice)) * parseInt(String(item.quantity)) * 100,
              ) / 100;
          });
          mfPrice = Math.round(mfPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = mfQuantity;

        if (mfPrice) {
          //orderPrice.itemPricingnFees = mfPrice; // Old logic
          if (
            typeof orderPrice.itemPricingnFees !== 'object' ||
            orderPrice.itemPricingnFees === null
          ) {
            orderPrice.itemPricingnFees = { itemPrice: {}, shippingFees: {} };
          }
          orderPrice.itemPricingnFees.itemPrice = mfPrice;
          orderPrice.itemPricingnFees.shippingFees =
            PROGRAM_VENDOR_CONFIG.DEFAULT.MISFITS.PRICE.DEFAULT_SHIPPING_PRICE;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
        }
        break;
      }

      case 'FFSH':
      case 'SSHE': {
        let ffshQuantity = 0;
        let ffPrice = 0;

        ordersItems.forEach((item) => {
          ffshQuantity += parseInt(String(item.quantity));
        });

        try {
          ordersItems.forEach((item) => {
            ffPrice +=
              Math.round(
                parseFloat(String(item.itemPrice)) * parseInt(String(item.quantity)) * 100,
              ) / 100;
          });
          ffPrice = Math.round(ffPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = ffshQuantity;

        if (ffPrice) {
          orderPrice.itemPricingnFees = ffPrice;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
        }
        break;
      }

      case 'GB': {
        let gbQuantity = 0;
        let gbPrice = 0;

        ordersItems.forEach((item) => {
          gbQuantity += parseInt(String(item.quantity));
        });

        try {
          ordersItems.forEach((item) => {
            gbPrice +=
              Math.round(
                parseFloat(String(item.itemPrice)) * parseInt(String(item.quantity)) * 100,
              ) / 100;
          });
          gbPrice = Math.round(gbPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = gbQuantity;

        if (gbPrice) {
          orderPrice.itemPricingnFees = gbPrice;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
        }
        break;
      }

      // The below FF calculator logic is obsolete now. This whole logic applies to the invoice (not per order)
      case 'FF': {
        const ffPricing = vendorsFoodPrice['FF'];
        let ffQuantity = 0;
        ordersItems.forEach((item) => {
          ffQuantity += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
        });

        orderPrice.quantity = ffQuantity;

        if (ffQuantity && ffQuantity > 0 && ffPricing !== undefined) {
          orderPrice.itemPricingnFees = ffPricing;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Issue with order quantity or pricing!`;
        }
        break;
      }

      case 'MH':
      case 'MHSC':
      case 'MHNC':
      case 'MHCA': {
        const mhPricing = vendorsFoodPrice[vendor];
        let mhQuantity1 = 0;

        try {
          ordersItems.forEach((item) => {
            mhQuantity1 += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
            //mhPrice += Math.round(parseFloat(item.price) * parseInt(item.quantity) * 100) / 100;
          });

          if (
            mhPricing!.itemPrice[mhQuantity1] &&
            mhPricing!.itemPrice[mhQuantity1] !== undefined
          ) {
            orderPrice.itemPricingnFees = mhPricing!;
          } else {
            orderPrice.hasErrors = true;
            const _quantities = Object.keys(mhPricing!.itemPrice);
            orderPrice.message = `Error - Pricing not found for the total items selected`;
          }

          //mhPrice = Math.round(mhPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = mhQuantity1;

        /*
         * SUSPECTED DEFECT (MH order priced with an absent pricing table) — REPORTED,
         * NOT FIXED. This guard tests the QUANTITY, not the pricing table, and it sits
         * OUTSIDE the `try` above — so an `MH` order admitted by name with no
         * `vendorsFoodPrice['MH']` entry reaches here with `mhPricing === undefined`
         * and stores that undefined as the order's `itemPricingnFees`.
         *
         * `foodPricingCalc`'s `MH` branch then reads `itemPrice[quantity]` off it and
         * throws inside the price calculation, where the `try` that would have turned
         * this into "Error - Price is missing for the food item" has already been left.
         * The sibling read at the top of the branch is inside the `try` and degrades
         * gracefully; this one does not.
         *
         * The assertion records that the store happens regardless, preserving today's
         * behaviour. Testing `mhPricing` here instead would convert the throw into the
         * quantity-error message — a different outcome for a real order.
         */
        if (mhQuantity1 && mhQuantity1 > 0 && mhQuantity1 !== undefined) {
          orderPrice.itemPricingnFees = mhPricing!;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Issue with order quantity or pricing!`;
        }
        break;
      }

      case 'OK': {
        let okQuantity = 0;
        let okPrice = 0;

        ordersItems.forEach((item) => {
          okQuantity += parseInt(String(item.quantity));
        });

        try {
          ordersItems.forEach((item) => {
            okPrice +=
              Math.round(
                parseFloat(String(item.itemPrice)) * parseInt(String(item.quantity)) * 100,
              ) / 100;
          });
          okPrice = Math.round(okPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = okQuantity;

        if (okPrice) {
          orderPrice.itemPricingnFees = okPrice;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
        }
        if (orderPrice.quantity < 5 || orderPrice.quantity > 10) {
          orderPrice.hasErrors = true;
          orderPrice.message =
            orderPrice.message + `Error - Please only order OK in between 5 and 10 SERVINGS`;
        }
        break;
      }
      case 'ML': {
        const mlPricing = vendorsFoodPrice['ML'];
        let mlQuantity = 0;
        ordersItems.forEach((item) => {
          mlQuantity += parseInt(String(item.quantity)) * parseInt(String(item.noOfServings));
        });

        orderPrice.quantity = mlQuantity;

        if (mlPricing!.itemPrice[mlQuantity] && mlPricing!.itemPrice[mlQuantity] !== undefined) {
          orderPrice.itemPricingnFees = mlPricing!;
        } else {
          orderPrice.hasErrors = true;
          const quantities = Object.keys(mlPricing!.itemPrice);
          orderPrice.message = `Error - Please only order ML in quantities of ${quantities.join(', ')} SERVINGS`;
        }
        break;
      }
      case VENDORS.NU:
      case VENDORS.CCF:
      case VENDORS.MM:
      case VENDORS.HH: {
        let NuQuantity = 0;
        let nuPrice = 0;

        ordersItems.forEach((item) => {
          NuQuantity += parseInt(String(item.quantity));
        });

        try {
          ordersItems.forEach((item) => {
            nuPrice +=
              Math.round(
                parseFloat(String(item.itemPrice)) * parseInt(String(item.quantity)) * 100,
              ) / 100;
          });
          nuPrice = Math.round(nuPrice * 100) / 100;
        } catch {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
          return orderPrice;
        }

        orderPrice.quantity = NuQuantity;

        if (nuPrice) {
          orderPrice.itemPricingnFees = nuPrice;
        } else {
          orderPrice.hasErrors = true;
          orderPrice.message = `Error - Price is missing for the food item`;
        }
        break;
      }
      default: {
        const defaultPricing = vendorsFoodPrice[vendor];
        let defaultQuantity = 0;
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. The `1 *` binds tighter than the
         * `?:`, so the second arm reads `(1 * item?.noOfServings) ? … : 1` — a
         * multiplication whose only purpose is to be tested for truthiness, and
         * which is `NaN` (falsy) whenever `noOfServings` is absent or
         * non-numeric. An item with a `noOfServings` of `'0'` also lands on the
         * `: 1` arm. The stray `1 *` reads as an editing residue rather than
         * intent.
         *
         * `noOfServings` is optional on the item, so `parseInt` on it is
         * `parseInt(undefined)` in principle; it is unreachable only because the
         * `NaN` test above rejects exactly that case first. Both are left as they
         * are because changing either moves the quantity used to price an order.
         */
        ordersItems.forEach((item) => {
          // `item?.` on both receivers, matching upstream. Dropping the `?.` survives type
          // erasure: on a nullish element upstream contributes 1 and keeps pricing, while the
          // bare `.` throws out of the forEach and unwinds through `prepareOrderQuantity` and
          // `pricingCalculator` — a crashed food catalogue instead of a priced order. Measured
          // over 10 `ordersItems` shapes: identical on all 7 non-nullish, 3 divergent on a
          // nullish element. The `String()` wraps and the casts are inert and stay.
          defaultQuantity += item?.quantity
            ? parseInt(String(item.quantity))
            : 1 * (item?.noOfServings as number)
              ? parseInt(String(item?.noOfServings))
              : 1;
        });

        orderPrice.quantity = defaultQuantity;

        if (
          defaultPricing!.itemPrice[defaultQuantity] &&
          defaultPricing!.itemPrice[defaultQuantity] !== undefined
        ) {
          orderPrice.itemPricingnFees = defaultPricing!;
        } else {
          orderPrice.hasErrors = true;
          const quantities = Object.keys(defaultPricing!.itemPrice);
          orderPrice.message = `Error - Please only order ML in quantities of ${quantities.join(', ')} SERVINGS`;
        }
        break;
      }
    }
  }
  return orderPrice;
}

/**
 * Food pricing calculator.
 *
 * SUSPECTED DEFECT: this returns `undefined` when the order has no errors but
 * `orderPrice.itemPricingnFees` or `orderPrice.quantity` is falsy — neither branch
 * of the `if/else if` matches and control falls off the end. Callers already write
 * `validPrice?.foodPrice` (see `food/orders/index.tsx:368`,
 * `food/draftOrders/index.tsx:401` and `:501`, `food/catalog/item/foodBundleItems.tsx:982`),
 * so an unpriced order silently records `foodPricing: undefined` rather than
 * failing. Note `itemPricingnFees` starts at `0`, so a zero-priced order takes this
 * path. Left as-is: returning a zeroed price here would change behaviour.
 */
export function pricingCalculator(
  organization: string,
  vendor: string,
  ordersItems: PricedOrderItem[],
  vendorsFoodPrice: VendorPricingByCode,
  bundleName: string | undefined = '',
): CalculatedFoodPrice | undefined {
  const calculatedPrice = {
    foodPrice: 0,
    shippingFees: 0,
    message: '',
    hasErrors: false,
    quantity: 0,
  };
  const orderPrice = prepareOrderQuantity(
    organization,
    vendor,
    ordersItems,
    vendorsFoodPrice,
    bundleName,
  );
  if (orderPrice.hasErrors) {
    calculatedPrice.hasErrors = true;
    calculatedPrice.message = orderPrice.message;
    return calculatedPrice;
  } else if (orderPrice.itemPricingnFees && orderPrice.quantity) {
    return foodPricingCalc(vendor, orderPrice, calculatedPrice, bundleName);
  }
}

/**
 * Resolves `orderPrice.itemPricingnFees` into a flat food price and shipping fee.
 *
 * The `vendor` argument is the discriminant for `itemPricingnFees`: `validateOrderQuantity`
 * stores a bare `number` for the per-item vendors (`CJ`, `MF`, `GB`, `OK`, `FFSH`, `SSHE`,
 * `NU`, `CCF`, `MM`, `HH`), a whole `VendorPricing` table for the table-priced vendors
 * (`SB`, `EW`, `ML`, `FFH`, `EF`, `MH`, `FF`), and a `ScalarItemPricing` for `PK`/`MF`.
 * Because the discriminant lives on a sibling parameter rather than on the value, TypeScript
 * cannot narrow `pricing` from the switch, so each branch reports TS2339 for the members it
 * reads. Those reads are correct for the variant that branch actually receives.
 *
 * SUSPECTED DEFECT (`CJ` / `OK` / `GB` / `FFSH` / `NU` family): these branches assign
 * `price.foodPrice = pricing` where `pricing` is the summed number — fine — but the shared
 * `default` branch and the table-priced branches read `pricing.itemPrice[quantity]`. A vendor
 * that reaches `default` while holding a number (any vendor code not listed in the switch but
 * admitted by `validateOrderQuantity`) reads `.itemPrice` off a number, yielding `undefined`
 * and a silent `foodPrice` of `0` rather than an error.
 *
 * Not fixed here: narrowing would require either a runtime `typeof` test the code does not
 * perform (a behaviour change) or an unproven assertion.
 */
export function foodPricingCalc(
  vendor: string,
  orderPrice: OrderQuantityPricing,
  price: CalculatedFoodPrice,
  bundleName: string | undefined,
): CalculatedFoodPrice {
  const pricing = orderPrice.itemPricingnFees;
  const quantity = orderPrice.quantity;

  /*
   * The three views of `pricing` the branches below read, named once.
   *
   * `vendor` is the discriminant but it sits on a sibling parameter, so no `switch`
   * on it can narrow `pricing`. These aliases state which variant each branch family
   * receives, as established by the matching `case` in `validateOrderQuantity`:
   *
   *   tablePricing  — `VendorPricing`, a per-quantity lookup table, for the
   *                   table-priced vendors (SB, EW, ML, FFH, EF, MH*, FF) and the
   *                   `default` branch.
   *   scalarPricing — `ScalarItemPricing`, already-resolved scalars, for PK and MF.
   *   summedPrice   — a bare `number`, the summed order price, for the per-item
   *                   vendors (CJ, FFSH, SSHE, NU, CCF, MM, HH, GB, OK).
   *
   * They are declarations, not runtime tests: no branch gains or loses a check, and
   * a branch reached while holding the wrong variant misbehaves exactly as it does
   * today — which is the SUSPECTED DEFECT recorded above the function.
   */
  const tablePricing = pricing as VendorPricing;
  const scalarPricing = pricing as ScalarItemPricing;
  const summedPrice = pricing as number;

  switch (vendor) {
    case 'PK':
      if (scalarPricing?.itemPrice) {
        price.foodPrice = scalarPricing.itemPrice;
        price.shippingFees = scalarPricing.shippingFees;
      }
      break;
    case 'CJ':
      price.foodPrice = summedPrice;
      //included ship fee in price
      price.shippingFees = 0;
      break;

    case 'EW':
      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = Math.round(tablePricing.itemPrice[quantity] * 100) / 100;
        price.shippingFees =
          (tablePricing.shippingFees[quantity] ? tablePricing.shippingFees[quantity] : 0) *
          quantity;
      }
      break;
    case 'SB':
      price.foodPrice = tablePricing.itemPrice[quantity] ? tablePricing.itemPrice[quantity] : 0;
      price.shippingFees = tablePricing.shippingFees[quantity]
        ? tablePricing.shippingFees[quantity]
        : 0;

      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = tablePricing.itemPrice[quantity]!;
        price.shippingFees = tablePricing.shippingFees[quantity]
          ? tablePricing.shippingFees[quantity]
          : 0;
      }
      break;

    case 'MF': {
      price.foodPrice = scalarPricing.itemPrice;
      const shippingFeesPerItem = PROGRAM_VENDOR_CONFIG.DEFAULT.MISFITS.PRICE.DEFAULT_SHIPPING_PRICE;
      price.shippingFees = parseFloat(String(shippingFeesPerItem));
      break;
    }

    case 'FFSH':
      price.foodPrice = summedPrice;
      price.shippingFees = PROGRAM_VENDOR_CONFIG.DEFAULT.FFSH.PRICE.DEFAULT_SHIPPING_PRICE;
      break;
    case 'SSHE':
      price.foodPrice = summedPrice;
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. The `?.` after `SSHE` is followed
       * by an UNGUARDED `.DEFAULT_SHIPPING_PRICE`, so the optional chain buys
       * nothing: an SSHE order whose bundle name is absent from the per-bundle
       * table throws here, inside the pricing calculator.
       *
       * `mapKey` names the subscript coercion (`bundleName` is optional, and
       * `undefined?.toUpperCase()` is itself `undefined`); the `!` keeps the
       * throw. Completing the chain would return `undefined` shipping fees into a
       * price the order is then placed at, which is a silently wrong total.
       */
      price.shippingFees = (
        PROGRAM_VENDOR_CONFIG.DEFAULT.SSHE as Record<string, VendorDefaultPrice | undefined>
      )?.[mapKey(bundleName?.toUpperCase())]!.DEFAULT_SHIPPING_PRICE;
      break;
    case VENDORS.NU:
    case VENDORS.CCF:
    case VENDORS.MM:
    case VENDORS.HH:
      price.foodPrice = summedPrice;
      price.shippingFees = 0;
      break;
    case 'GB':
      price.foodPrice = summedPrice;
      price.shippingFees = 0;
      break;

    case 'MH':
      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = Math.round(tablePricing.itemPrice[quantity] * 100) / 100;
        price.shippingFees = tablePricing.shippingFees[quantity]
          ? tablePricing.shippingFees[quantity]
          : 0;
      }
      break;

    case 'MHNC':
    case 'MHCA':
      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = Math.round(tablePricing.itemPrice[quantity] * 100) / 100;
        price.shippingFees = tablePricing.shippingFees[quantity]
          ? tablePricing.shippingFees[quantity]
          : 0;
      }
      break;

    case 'MHSC':
      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = Math.round(tablePricing.itemPrice[quantity] * 100) / 100;
        // This condition is only for SC Bundles of Modify Health
        const currentTimeInEST = moment().tz('America/New_York').format('h:mma');
        const orderCreationTimeInEST = moment(currentTimeInEST, 'h:mma');
        const cutOffTime = moment('12:45pm', 'h:mma');
        if (orderCreationTimeInEST.isBefore(cutOffTime)) {
          price.shippingFees = 0;
        } else {
          price.shippingFees = 15.55;
        }
      }
      break;

    /*
     * SUSPECTED DEFECT (FF shipping fee can be `undefined`) — REPORTED, NOT FIXED.
     * Unlike every other table-priced branch, these three reads are unguarded and use
     * FIXED keys (150 / 100 / 1) rather than the order's quantity. An `FF` pricing
     * table whose `shippingFees` lacks the matching tier yields `undefined`, which is
     * stored straight into `price.shippingFees` — declared `number` on
     * `CalculatedFoodPrice`. Consumers then add it to the food price, and
     * `number + undefined` is `NaN`, so the invoice total renders as `NaN` rather
     * than erroring.
     *
     * The assertions record that the store happens unguarded, preserving today's
     * behaviour. Defaulting to `0` would invent a shipping fee for a real invoice.
     */
    case 'FF': // The below FF calculator logic is obsolete now apart from shipping fee. This whole logic applies to the invoice (not per order)
      if (quantity >= 150) {
        // price.foodPrice = Math.round((pricing.itemPrice[150] * quantity) * 100)/100;
        price.shippingFees = tablePricing.shippingFees[150]!;
      } else if (quantity >= 100) {
        // price.foodPrice = Math.round((pricing.itemPrice[100] * quantity) * 100)/100;
        price.shippingFees = tablePricing.shippingFees[100]!;
      } else if (quantity >= 1) {
        // price.foodPrice = Math.round((pricing.itemPrice[1] * quantity) * 100)/100;
        price.shippingFees = tablePricing.shippingFees[1]!;
      }
      break;

    case 'OK':
      price.foodPrice = summedPrice;
      price.shippingFees = 0;
      break;

    case 'ML':
    case 'FFH':
    case VENDORS.EF:
      price.foodPrice = tablePricing.itemPrice[quantity] ? tablePricing.itemPrice[quantity] : 0;
      price.shippingFees = tablePricing.shippingFees[quantity]
        ? tablePricing.shippingFees[quantity]
        : 0;

      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = tablePricing.itemPrice[quantity]!;
        price.shippingFees = tablePricing.shippingFees[quantity]
          ? tablePricing.shippingFees[quantity]
          : 0;
      }
      break;

    default:
      price.foodPrice = tablePricing.itemPrice[quantity] ? tablePricing.itemPrice[quantity] : 0;
      price.shippingFees = tablePricing.shippingFees[quantity]
        ? tablePricing.shippingFees[quantity]
        : 0;

      if (tablePricing.itemPrice[quantity]) {
        price.foodPrice = tablePricing.itemPrice[quantity]!;
        price.shippingFees = tablePricing.shippingFees[quantity]
          ? tablePricing.shippingFees[quantity]
          : 0;
      }
      break;
  }
  price.quantity = orderPrice.quantity;
  return price;
}
